import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { apiGet } from '../../../lib/api';

interface Redemption {
  id: string;
  rewardName: string;
  pointsCost: number;
  status: string;
  redeemedAt: string;
  fulfilledAt: string | null;
}

interface LoyaltyRedemptionHistoryProps {
  title?: string;
  maxItems?: number;
}

interface LoyaltyFullData {
  enrolled: boolean;
  redemptions: Redemption[];
}

const STATUS_STYLE: Record<string, { bg: string; text: string; label: string }> = {
  pending: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pending' },
  fulfilled: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Fulfilled' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-600', label: 'Cancelled' },
};

export function LoyaltyRedemptionHistoryBlock({ props }: { props: LoyaltyRedemptionHistoryProps }) {
  const { isAuthenticated } = useAuth();
  const title = props.title ?? 'Redemption History';
  const maxItems = props.maxItems ?? 10;

  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    setLoading(true);
    apiGet<LoyaltyFullData>('/customer/loyalty')
      .then((res) => {
        if (!cancelled) setRedemptions(res.redemptions ?? []);
      })
      .catch(() => { if (!cancelled) setRedemptions([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  if (loading) {
    return (
      <div className="loyalty-card p-6 animate-pulse">
        <div className="h-5 w-40 bg-[var(--color-surface-border)] rounded mb-4" />
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-[var(--color-surface-border)] rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="loyalty-card p-8 text-center">
        <div className="text-4xl mb-3 opacity-50">&#128230;</div>
        <h2 className="loyalty-heading text-xl mb-2 text-[var(--color-text)]">
          Login to view redemptions
        </h2>
        <p className="text-[var(--color-text-muted)] text-sm mb-4">
          Sign in to see your reward redemption history.
        </p>
        <a
          href="/login"
          className="inline-block px-6 py-2 rounded-lg bg-[var(--color-primary)] text-white font-semibold text-sm hover:opacity-90 transition-opacity"
        >
          Sign In
        </a>
      </div>
    );
  }

  const display = redemptions.slice(0, maxItems);

  return (
    <div>
      <h2 className="loyalty-heading text-lg text-[var(--color-text)] mb-4">{title}</h2>
      {display.length > 0 ? (
        <div className="loyalty-card overflow-hidden">
          <div className="divide-y divide-[var(--color-surface-border)]">
            {display.map((r) => {
              const style = STATUS_STYLE[r.status] ?? STATUS_STYLE.pending;
              return (
                <div key={r.id} className="flex items-center gap-3 px-4 py-3">
                  <span className="text-lg flex-shrink-0">&#127873;</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[var(--color-text)] truncate">
                      {r.rewardName}
                    </div>
                    <div className="text-[10px] text-[var(--color-text-muted)]">
                      {new Date(r.redeemedAt).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </div>
                  </div>
                  <span className="text-sm font-bold text-[var(--color-text-muted)] flex-shrink-0">
                    -{r.pointsCost.toLocaleString()} pts
                  </span>
                  <span
                    className={`flex-shrink-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${style.bg} ${style.text}`}
                  >
                    {style.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="loyalty-card p-6 text-center text-[var(--color-text-muted)] text-sm">
          No redemptions yet. Visit the Rewards page to redeem points!
        </div>
      )}
    </div>
  );
}
